import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { nav, site, whatsappEnquiry } from '../data/site';
import { Icon } from './Primitives';

/**
 * Solid cream masthead.
 *
 * It used to float transparently over the hero, but the hero photographs open on
 * a bright studio backdrop, so the bar washed out into a grey band with the white
 * logo barely legible. Cream reads cleanly on every page and lets the photography
 * start below it.
 *
 * The navy info strip that sat above this was removed on request. The phone number
 * it carried is kept as a discreet header link so a walk-in customer still has a
 * one-tap number without scrolling to the footer.
 */
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Only used to lift the bar off the page once you start scrolling.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the drawer on navigation, and lock the page behind it while open.
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <header
        className={`sticky top-0 z-40 border-b bg-cream-50 transition-shadow duration-500 ease-luxe ${
          scrolled
            ? 'border-royal-800/10 shadow-[0_10px_30px_-24px_rgba(37,34,70,0.5)]'
            : 'border-royal-800/[0.07]'
        }`}
      >
        <div className="shell flex items-center justify-between gap-4 py-3.5">
          <Link to="/" aria-label={`${site.name} — home`} className="shrink-0">
            <img
              src="/assets/logo/logo-horizontal-maroon.png"
              alt={`${site.name}, since ${site.since}`}
              width="900"
              height="235"
              className="h-9 w-auto sm:h-11"
            />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  [
                    'link-underline py-1 text-[11px] font-medium uppercase tracking-wide2 transition-colors duration-300',
                    isActive ? 'text-maroon-700' : 'text-royal-800/75 hover:text-maroon-700',
                  ].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={site.phoneHref}
              className="link-underline hidden text-[11px] font-medium uppercase tracking-wide2
                text-royal-800/75 transition-colors hover:text-maroon-700 md:inline-flex"
            >
              <Icon.Phone className="h-3.5 w-3.5" />
              {site.phone}
            </a>

            <a
              href={whatsappEnquiry()}
              target="_blank"
              rel="noreferrer noopener"
              className="btn-primary hidden !px-5 !py-3 sm:inline-flex"
            >
              <Icon.WhatsApp className="h-4 w-4" />
              Enquire
            </a>

            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              className="grid h-11 w-11 place-items-center border border-royal-800/20 text-royal-800
                transition-colors duration-300 hover:bg-royal-800 hover:text-cream-50 lg:hidden"
            >
              <span className="sr-only">Menu</span>
              <span aria-hidden="true" className="flex flex-col gap-[5px]">
                <span className="block h-px w-5 bg-current" />
                <span className="block h-px w-5 bg-current" />
                <span className="block h-px w-3.5 bg-current" />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden ${open ? '' : 'pointer-events-none'}`}
        aria-hidden={!open}
      >
        <button
          type="button"
          tabIndex={open ? 0 : -1}
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className={`absolute inset-0 bg-royal-950/70 backdrop-blur-sm transition-opacity duration-500 ${
            open ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          className={`noise-navy absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col
            border-l border-gold-500/20 transition-transform duration-500 ease-luxe ${
              open ? 'translate-x-0' : 'translate-x-full'
            }`}
        >
          <div className="flex items-center justify-between border-b border-cream-50/10 px-6 py-4">
            <img
              src="/assets/logo/monogram-white.png"
              alt=""
              width="600"
              height="480"
              className="h-9 w-auto opacity-90"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              tabIndex={open ? 0 : -1}
              className="grid h-10 w-10 place-items-center border border-cream-50/20 text-cream-50 hover:bg-cream-50/10"
            >
              <Icon.Close className="h-4 w-4" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 py-6" aria-label="Mobile">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                tabIndex={open ? 0 : -1}
                className={({ isActive }) =>
                  [
                    'flex items-center justify-between border-b border-cream-50/10 py-4 font-display text-2xl font-light transition-colors',
                    isActive ? 'text-gold-300' : 'text-cream-100 hover:text-gold-300',
                  ].join(' ')
                }
              >
                {item.label}
                <Icon.Chevron className="h-4 w-4 opacity-50" />
              </NavLink>
            ))}
          </nav>

          <div className="space-y-3 border-t border-cream-50/10 px-6 py-6">
            <a
              href={whatsappEnquiry()}
              target="_blank"
              rel="noreferrer noopener"
              tabIndex={open ? 0 : -1}
              className="btn-gold w-full"
            >
              <Icon.WhatsApp className="h-4 w-4" />
              Enquire on WhatsApp
            </a>
            <a href={site.phoneHref} tabIndex={open ? 0 : -1} className="btn-outline-light w-full">
              <Icon.Phone className="h-4 w-4" />
              {site.phone}
            </a>
            <div className="flex items-center justify-center gap-4 pt-2">
              <a
                href={site.social.instagram}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Instagram"
                tabIndex={open ? 0 : -1}
                className="text-cream-200/70 hover:text-gold-300"
              >
                <Icon.Instagram className="h-5 w-5" />
              </a>
              <a
                href={site.social.facebook}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Facebook"
                tabIndex={open ? 0 : -1}
                className="text-cream-200/70 hover:text-gold-300"
              >
                <Icon.Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
